import { BATHE_MANAGEMENT } from '@core/constants';
import { buildTokenizer, convertToHiragana, DynamoDBKeyHelper, convertHalfWidthToFullWidth } from '@core/helper';
import { BatheType, BatheMethodOption, ExtendedBathDaiModel, BathDailyModel, BathDaiModel, BathRoomSummary, BathRoomRule, BathRule, BathDais, BathMasterItem, BatheImplement, StartOfWeek, UserYoteiConfig, DAIS, BathDailyItem } from '@core/models/models/bathe.model';
import { environment } from '@environments/environment';
import { OfficeType } from '@core/constants';
import { Utils } from '@core/helper';
import { bathRuleToJson } from '@modules/title-settings/bath-settings/bath-info/bath-rule.helper';
import { DayColorType, Holiday, SidebarBlock, Tag, WeeklyColumn } from '@core/models/models/bathe-management.model';

export const DAYS_OF_WEEK = ['月', '火', '水', '木', '金', '土', '日'];
export const SUCCESS_STATUS = [200, 204] as const;

/**
 * Whether one upsert-logs item was actually stored. Read fail-closed: only an explicit `written`
 * (or a missing `outcome`, for a server that predates the field) counts. Everything else -
 * `conflict`, `failed`, an unknown outcome, or a missing item altogether (undefined `r`, which
 * happens when the response had no entry or a non-200 status was mapped to an empty list) - means
 * the write did not land, so the day must be reloaded rather than shown as saved.
 */
export function isWrittenUpsertResult(r: { outcome?: string } | undefined): boolean {
    return !!r && (r.outcome === undefined || r.outcome === 'written');
}

/**
 * Helper class for bathe-related utility functions
 * Shared between bath-dai-setting and bathe-management components
 */
export class BatheHelper {
  /**
   * Convert bath days code to display format
   * Format: 0000000 (0 = false, 1 = true) representing Mon-Sun
   * @param bathDaysCode - Binary string representing selected days
   * @returns Formatted string like "月/水/金" or "毎日" if all days are selected
   */
  static getBasicBatheDate(bathDaysCode: string | null): string {
    if (!bathDaysCode?.trim()) return '';

    // Check if all 7 days are selected (1111111)
    const first7Days = bathDaysCode.slice(0, DAYS_OF_WEEK.length);
    if (first7Days === '1111111') return '毎日';

    const selectedDays = Array.from(bathDaysCode)
      .slice(0, DAYS_OF_WEEK.length)
      .map((char, index) => char === '1' ? DAYS_OF_WEEK[index] : null)
      .filter((day): day is string => day !== null);

    return selectedDays.join('/');
  }

  /**
   * Convert bath weeks code to display format for Tsusho
   * Format: 00000 (0 = false, 1 = true) representing weeks 1-5
   * @param bathWeeksCode - Binary string representing selected weeks
   * @returns Formatted string like "第1*2*3" or "毎週" if all weeks are selected
   */
  static formatBathWeeks(bathWeeksCode: string | null): string {
    if (!bathWeeksCode?.trim()) return '';

    // Check if all 5 weeks are selected (11111)
    const first5Weeks = bathWeeksCode.slice(0, 5);
    if (first5Weeks === '11111') return '毎週';

    const selectedWeeks = Array.from(bathWeeksCode)
      .slice(0, 5)
      .map((char, index) => char === '1' ? (index + 1).toString() : null)
      .filter((week): week is string => week !== null);

    return selectedWeeks.length > 0 ? `第${selectedWeeks.join('・')}` : '';
  }

  /**
   * Convert display string to BatheType enum value
   * @param priority - Display string like '最初', '優先', '最後'
   * @returns BatheType enum value
   */
  static getBatheTypeFromPriority(priority: string): BatheType {
    const typeMap: { [key: string]: BatheType } = {
      '最初': BatheType.First,
      '優先': BatheType.Priority,
      '最後': BatheType.Last
    };
    return typeMap[priority] ?? BatheType.None;
  }

  /**
   * Convert display string to BatheMethodOption enum value
   * @param method - Display string like '普通浴', '個浴', etc.
   * @returns BatheMethodOption enum value
   */
  static getBatheMethodOptionFromMethod(method: string): BatheMethodOption {
    const optionMap: { [key: string]: BatheMethodOption } = {
      '普通浴': BatheMethodOption.Normal,
      '個浴': BatheMethodOption.Private,
      'チェアー浴': BatheMethodOption.Chair,
      'リフト浴': BatheMethodOption.Lift,
      'ストレッチャー浴': BatheMethodOption.Stretcher,
      '臥床浴': BatheMethodOption.Bed,
      'シャワー浴': BatheMethodOption.Shower
    };
    return optionMap[method];
  }

  /**
   * Convert BatheType enum value to display string
   * @param batheType - BatheType enum value
   * @returns Display string like '最初', '優先', '最後'
   */
  static getPriorityFromBatheType(batheType: BatheType): string {
    const priorityMap: { [key: number]: string } = {
      [BatheType.First]: '最初',
      [BatheType.Priority]: '優先',
      [BatheType.Last]: '最後',
      [BatheType.None]: ''
    };
    return priorityMap[batheType] ?? '';
  }

  /**
   * Convert BatheMethodOption enum value to display string
   * @param option - BatheMethodOption enum value
   * @returns Display string like '普通浴', '個浴', etc.
   */
  static getMethodFromBatheOption(option: BatheMethodOption | null): string {
    if (!option) return '';

    const methodMap: { [key: number]: string } = {
      [BatheMethodOption.Normal]: '普通浴',
      [BatheMethodOption.Private]: '個浴',
      [BatheMethodOption.Chair]: 'チェアー浴',
      [BatheMethodOption.Lift]: 'リフト浴',
      [BatheMethodOption.Stretcher]: 'ストレッチャー浴',
      [BatheMethodOption.Bed]: '臥床浴',
      [BatheMethodOption.Shower]: 'シャワー浴'
    };
    return methodMap[option];
  }

  /**
   * Populate user information (name, gender, avatar) from daiInfo
   * @param extendedModel - Extended bath dai model to populate
   * @param daiInfo - Array of dai information objects
   */
  static populateDaiInfo(extendedModel: ExtendedBathDaiModel, daiInfo: any[], tokenizer: any): void {
    const daiObj = daiInfo.find((x: any) => x.daicd === extendedModel.daiCd);

    if (daiObj) {
      extendedModel.name = `${daiObj.family || ''} ${daiObj.given || ''}`.trim();
      extendedModel.gender = daiObj.sex === 1 ? '男' : '女';
      extendedModel.daiFamily = daiObj.family ?? "";
      extendedModel.daiGiven = daiObj.given ?? "";
      extendedModel.fullNameHiragana = convertToHiragana(`${daiObj.kanafamily || ''} ${daiObj.kanagiven || ''}`.trim(), tokenizer) ?? "";
      extendedModel.avatar = daiObj.avatarSource?.trim()
        ? `${environment.avatarUrl}/${daiObj.avatarSource}`
        : null;
    }
  }

  /**
   * Katakana to Hiragana conversion map
   * Used to convert katakana to hiragana for grouping purposes
   */
  private static readonly KATAKANA_TO_HIRAGANA: { [key: string]: string } = {
    // ア行 (a-group)
    'ア': 'あ', 'イ': 'い', 'ウ': 'う', 'エ': 'え', 'オ': 'お',
    // カ行 (ka-group) - includes voiced variants ガ, ギ, グ, ゲ, ゴ
    'カ': 'か', 'キ': 'き', 'ク': 'く', 'ケ': 'け', 'コ': 'こ',
    'ガ': 'が', 'ギ': 'ぎ', 'グ': 'ぐ', 'ゲ': 'げ', 'ゴ': 'ご',
    // サ行 (sa-group) - includes voiced variants ザ, ジ, ズ, ゼ, ゾ
    'サ': 'さ', 'シ': 'し', 'ス': 'す', 'セ': 'せ', 'ソ': 'そ',
    'ザ': 'ざ', 'ジ': 'じ', 'ズ': 'ず', 'ゼ': 'ぜ', 'ゾ': 'ぞ',
    // タ行 (ta-group) - includes voiced variants ダ, ヂ, ヅ, デ, ド
    'タ': 'た', 'チ': 'ち', 'ツ': 'つ', 'テ': 'て', 'ト': 'と',
    'ダ': 'だ', 'ヂ': 'ぢ', 'ヅ': 'づ', 'デ': 'で', 'ド': 'ど',
    // ナ行 (na-group)
    'ナ': 'な', 'ニ': 'に', 'ヌ': 'ぬ', 'ネ': 'ね', 'ノ': 'の',
    // ハ行 (ha-group) - includes voiced variants バ, ビ, ブ, ベ, ボ and semi-voiced パ, ピ, プ, ペ, ポ
    'ハ': 'は', 'ヒ': 'ひ', 'フ': 'ふ', 'ヘ': 'へ', 'ホ': 'ほ',
    'バ': 'ば', 'ビ': 'び', 'ブ': 'ぶ', 'ベ': 'べ', 'ボ': 'ぼ',
    'パ': 'ぱ', 'ピ': 'ぴ', 'プ': 'ぷ', 'ペ': 'ぺ', 'ポ': 'ぽ',
    // マ行 (ma-group)
    'マ': 'ま', 'ミ': 'み', 'ム': 'む', 'メ': 'め', 'モ': 'も',
    // ヤ行 (ya-group)
    'ヤ': 'や', 'ユ': 'ゆ', 'ヨ': 'よ',
    // ラ行 (ra-group)
    'ラ': 'ら', 'リ': 'り', 'ル': 'る', 'レ': 'れ', 'ロ': 'ろ',
    // ワ行 (wa-group)
    'ワ': 'わ', 'ヰ': 'ゐ', 'ヱ': 'ゑ', 'ヲ': 'を', 'ン': 'ん',
    // Small characters (ャ, ュ, ョ, ッ, etc.)
    'ャ': 'や', 'ュ': 'ゆ', 'ョ': 'よ', 'ッ': 'つ',
    'ァ': 'あ', 'ィ': 'い', 'ゥ': 'う', 'ェ': 'え', 'ォ': 'お'
  };

  /**
   * Hiragana character group definitions
   * Maps each hiragana character to its group
   */
  private static readonly HIRAGANA_GROUPS: { [key: string]: string } = {
    // あ行 (a-group)
    'あ': 'あ', 'い': 'あ', 'う': 'あ', 'え': 'あ', 'お': 'あ',
    // か行 (ka-group) - includes voiced variants が, ぎ, ぐ, げ, ご
    'か': 'か', 'き': 'か', 'く': 'か', 'け': 'か', 'こ': 'か',
    'が': 'か', 'ぎ': 'か', 'ぐ': 'か', 'げ': 'か', 'ご': 'か',
    // さ行 (sa-group) - includes voiced variants ざ, じ, ず, ぜ, ぞ
    'さ': 'さ', 'し': 'さ', 'す': 'さ', 'せ': 'さ', 'そ': 'さ',
    'ざ': 'さ', 'じ': 'さ', 'ず': 'さ', 'ぜ': 'さ', 'ぞ': 'さ',
    // た行 (ta-group) - includes voiced variants だ, ぢ, づ, で, ど
    'た': 'た', 'ち': 'た', 'つ': 'た', 'て': 'た', 'と': 'た',
    'だ': 'た', 'ぢ': 'た', 'づ': 'た', 'で': 'た', 'ど': 'た',
    // な行 (na-group)
    'な': 'な', 'に': 'な', 'ぬ': 'な', 'ね': 'な', 'の': 'な',
    // は行 (ha-group) - includes voiced variants ば, び, ぶ, べ, ぼ and semi-voiced ぱ, ぴ, ぷ, ぺ, ぽ
    'は': 'は', 'ひ': 'は', 'ふ': 'は', 'へ': 'は', 'ほ': 'は',
    'ば': 'は', 'び': 'は', 'ぶ': 'は', 'べ': 'は', 'ぼ': 'は',
    'ぱ': 'は', 'ぴ': 'は', 'ぷ': 'は', 'ぺ': 'は', 'ぽ': 'は',
    // ま行 (ma-group)
    'ま': 'ま', 'み': 'ま', 'む': 'ま', 'め': 'ま', 'も': 'ま',
    // や行 (ya-group)
    'や': 'や', 'ゆ': 'や', 'よ': 'や',
    // ら行 (ra-group)
    'ら': 'ら', 'り': 'ら', 'る': 'ら', 'れ': 'ら', 'ろ': 'ら',
    // わ行 (wa-group)
    'わ': 'わ', 'ゐ': 'わ', 'ゑ': 'わ', 'を': 'わ', 'ん': 'わ'
  };

  /**
   * Get the hiragana group character for a given name
   * Supports both hiragana and katakana input
   * @param name - Full name in hiragana or katakana
   * @returns Group character (あ, か, さ, た, な, は, ま, や, ら, わ) or empty string if no match
   */
  static getHiraganaGroup(name: string): string {
    if (!name || name.length === 0) {
      return '';
    }

    // Get the first character
    let firstChar = convertHalfWidthToFullWidth(name.charAt(0));
    
    // If it's katakana, convert to hiragana first
    if (this.KATAKANA_TO_HIRAGANA[firstChar]) {
      firstChar = this.KATAKANA_TO_HIRAGANA[firstChar];
    }

    // Return the group for this character
    return this.HIRAGANA_GROUPS[firstChar] || '';
  }

  /**
   * Filter records by hiragana group
   * @param records - Array of ExtendedBathDaiModel records
   * @param selectedChar - Selected hiragana group character (e.g., 'あ', 'か', etc.)
   * @returns Filtered array of records matching the selected group
   */
  static filterByHiraganaGroup(records: ExtendedBathDaiModel[], selectedChar: string): ExtendedBathDaiModel[] {
    if (!selectedChar) {
      return records;
    }

    return records.filter(record => {
      const group = this.getHiraganaGroup(record.fullNameHiragana || '');
      return group === selectedChar;
    });
  }

  /**
   * Sort records by hiragana group
   * Groups are ordered: あ, か, さ, た, な, は, ま, や, ら, わ
   * @param records - Array of ExtendedBathDaiModel records
   * @returns Sorted array of records grouped by hiragana character
   */
  static sortByHiraganaGroup(records: ExtendedBathDaiModel[]): ExtendedBathDaiModel[] {
    const GROUP_ORDER: { [key: string]: number } = {
      'あ': 1,
      'か': 2,
      'さ': 3,
      'た': 4,
      'な': 5,
      'は': 6,
      'ま': 7,
      'や': 8,
      'ら': 9,
      'わ': 10
    };

    return [...records].sort((a, b) => {
      const groupA = this.getHiraganaGroup(a.fullNameHiragana || '');
      const groupB = this.getHiraganaGroup(b.fullNameHiragana || '');

      const orderA = GROUP_ORDER[groupA] || 999;
      const orderB = GROUP_ORDER[groupB] || 999;

      if (orderA !== orderB) {
        return orderA - orderB;
      }

      // Within same group, sort alphabetically by hiragana name
      return (a.fullNameHiragana || '').localeCompare(b.fullNameHiragana || '', 'ja');
    });
  }

  /**
   * Get available hiragana groups from records
   * Returns set of group characters that have at least one record
   * @param records - Array of ExtendedBathDaiModel records
   * @returns Set of available group characters
   */
  static getAvailableHiraganaGroups(records: ExtendedBathDaiModel[]): Set<string> {
    const availableGroups = new Set<string>();

    records.forEach(record => {
      const group = this.getHiraganaGroup(record.fullNameHiragana || '');
      if (group) {
        availableGroups.add(group);
      }
    });

    return availableGroups;
  }

  /**
   * Find the index of the first record in a specific hiragana group
   * @param records - Array of ExtendedBathDaiModel records (must be sorted by hiragana group)
   * @param targetGroup - Target hiragana group character
   * @returns Index of first record in the group, or -1 if not found
   */
  static findFirstIndexOfGroup(records: ExtendedBathDaiModel[], targetGroup: string): number {
    return records.findIndex(record => {
      const group = this.getHiraganaGroup(record.fullNameHiragana || '');
      return group === targetGroup;
    });
  }

  static async sortKanaName(arr: any[] = []) {
    var tokenizer = await buildTokenizer();
    arr.sort((a, b) => {
      const nameA = `${convertToHiragana(a.kanafamily, tokenizer)}${convertToHiragana(a.kanagiven, tokenizer)}`;
      const nameB = `${convertToHiragana(b.kanafamily, tokenizer)}${convertToHiragana(b.kanagiven, tokenizer)}`;
      return nameA.localeCompare(nameB, 'ja');
    });
    return arr;
  }

  /**
   * Priority order mapping for sorting
   * Priority order: First (1) > Priority (2) > None (3) > Last (4)
   */
  public static readonly PRIORITY_ORDER: Readonly<{ [key: number]: number }> = {
    [BatheType.First]: 1,
    [BatheType.Priority]: 2,
    [BatheType.None]: 3,
    [BatheType.Last]: 4
  };

  /**
   * Sort data by priorityCd (BatheType enum values)
   * Priority order: First (1) > Priority (2) > None (3) > Last (4)
   * Within each priority group, sort by gender if bathRoomRule is specified
   * @param data - Array of data with priorityCd and gender fields
   * @param bathRoomRule - Optional bath room rule for gender sorting (WomenFirst, MenFirst, or None)
   * @returns Sorted array
   */
  static sortByPriority<T extends { priorityCd?: number | null; gender?: '男' | '女' }>(
    data: T[],
    bathRoomRule: BathRoomRule = BathRoomRule.None
  ): T[] {
    return [...data].sort((a, b) => {
      const priorityA = this.PRIORITY_ORDER[a.priorityCd ?? BatheType.None] ?? 999;
      const priorityB = this.PRIORITY_ORDER[b.priorityCd ?? BatheType.None] ?? 999;

      // First, sort by priority
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // Within same priority group, sort by gender if rule is specified
      if (bathRoomRule !== BathRoomRule.None && a.gender && b.gender) {
        const genderA = a.gender;
        const genderB = b.gender;

        // If both have same gender, maintain current order
        if (genderA === genderB) {
          return 0;
        }

        // WomenFirst: 女 comes before 男
        if (bathRoomRule === BathRoomRule.WomenFirst) {
          return genderA === '女' ? -1 : 1;
        }

        // MenFirst: 男 comes before 女
        if (bathRoomRule === BathRoomRule.MenFirst) {
          return genderA === '男' ? -1 : 1;
        }
      }

      // If no gender rule or missing gender data, maintain current order
      return 0;
    });
  }

  /**
   * Check if a user is short-term based on bathDays
   * @param item - Extended bath dai model
   * @returns true if user is short-term
   */
  static isShortTermUser(item: ExtendedBathDaiModel, officeType: OfficeType): boolean {
    if (Utils.isTsusho(officeType)) {
        return false; // Move logic from https://fds-ssc.backlog.jp/view/CKWEB-1483
    }
    // Helper function to check if string is null, empty, or all zeros
    const isNullOrEmptyOrAllZeros = (value: string | null | undefined): boolean => {
      if (Utils.isNullOrEmpty(value)) return true;
      // Check if all characters are '0'
      return value!.split('').every(char => char === '0');
    };
    return isNullOrEmptyOrAllZeros(item.bathDays);
  }

  /**
   * Format date string to MM/DD format
   * @param dateString - Date string to format
   * @returns Formatted date string (MM/DD) or empty string if invalid
   */
  static formatDateToMMDD(dateString: string | null | undefined): string {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';

      const month = date.getMonth() + 1;
      const day = date.getDate();
      return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  }

  /**
   * Calculate days difference from a date to today (or custom base date)
   * @param dateString - Date string to calculate from
   * @param baseDate - Base date to compare against (defaults to today)
   * @returns Days difference (positive = past, 0 = today, negative = future)
   */
  static calculateDaysDifference(dateString: string | null | undefined, baseDate?: Date): number | null {
    if (!dateString) return null;

    try {
      const targetDate = new Date(dateString);
      if (isNaN(targetDate.getTime())) return null;

      const base = baseDate || new Date();
      base.setHours(0, 0, 0, 0);
      targetDate.setHours(0, 0, 0, 0);

      const diffTime = base.getTime() - targetDate.getTime();
      return Math.round(diffTime / (1000 * 60 * 60 * 24));
    } catch (error) {
      console.error('Error calculating days difference:', error);
      return null;
    }
  }

  /**
   * Calculate reminder text in Japanese format
   * @param dateString - Date string
   * @param baseDate - Base date to compare against (defaults to today)
   * @returns Reminder text (e.g., "5日前", "今日", "3日後") or empty string if invalid
   */
  static calculateReminderText(dateString: string | null | undefined, baseDate?: Date): string {
    const daysDiff = this.calculateDaysDifference(dateString, baseDate);
    if (daysDiff === null) return '';

    if (daysDiff > 0) {
      return `${daysDiff}日前`;
    } else if (daysDiff === 0) {
      return '今日';
    } else {
      return `${Math.abs(daysDiff)}日後`;
    }
  }

  /**
   * Calculate formatted last bathe date and days ago text
   * @param lastBatheDate - Last bathe date string
   * @returns Object with formatted date and days ago text
   */
  static calculateLastBatheInfo(lastBatheDate: string | undefined): {
    lastBatheDateFormatted: string;
    lastBatheDaysAgo: string
  } {
    if (!lastBatheDate) {
      return { lastBatheDateFormatted: '', lastBatheDaysAgo: '' };
    }

    const lastDate = new Date(lastBatheDate);
    if (isNaN(lastDate.getTime())) {
      return { lastBatheDateFormatted: '', lastBatheDaysAgo: '' };
    }

    // Format as M/D (without padding)
    const lastBatheDateFormatted = `${lastDate.getMonth() + 1}/${lastDate.getDate()}`;

    // Calculate days difference
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    lastDate.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((today.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));

    let lastBatheDaysAgo = '';
    if (diffDays === 0) {
      lastBatheDaysAgo = '(本日)';
    } else if (diffDays === 1) {
      lastBatheDaysAgo = '(1日前)';
    } else if (diffDays > 1) {
      lastBatheDaysAgo = `(${diffDays}日前)`;
    }

    return { lastBatheDateFormatted, lastBatheDaysAgo };
  }

  /**
   * Parse bathroom summaries and convert bathRule XML to JSON
   * @param batheObjs - Raw bathroom objects from API
   * @returns Array of BathRoomSummary with parsed bathRuleXMLValue
   */
  static parseBathroomSummaries(batheObjs: any[]): BathRoomSummary[] {
    return batheObjs.map((room: any) => ({
      ...room,
      bathRuleXMLValue: room.bathRule ? bathRuleToJson(room.bathRule) : null
    })) as BathRoomSummary[];
  }

  /**
   * Parse bathroom summaries from new API response (BathMaster[])
   * Converts BathMaster objects to BathRoomSummary with parsed bathRuleXMLValue
   * @param bathMasters - BathMaster array from new API (getBathroomSummary)
   * @returns Array of BathRoomSummary with parsed bathRuleXMLValue
   */
  static parseBathMastersToBathRoomSummaries(bathMasters: any[]): BathRoomSummary[] {
    return bathMasters.map((master: any) => {
      // Extract IDs from DynamoDB keys
      const corpCd = master.PartitionKey ? DynamoDBKeyHelper.getValueByClass(master.PartitionKey, "corp") || '' : '';
      const officeCd = master.PartitionKey ? DynamoDBKeyHelper.getValueByClass(master.PartitionKey, "office") || '' : '';
      const bathRoomCd = master.SortKey ? DynamoDBKeyHelper.getValueByClass(master.SortKey, "bathroom") : null;

      // Parse DaiTable CSV to get array of BathDais
      const bathDaisArray = parseDaiTableToBathDais(master.DaiTable || '');

      // Extract daiList (array of DAICD strings) from parsed BathDais
      const daiList = bathDaisArray
        .map((dai: any) => dai.DAICD)
        .filter((daicd: string | undefined): daicd is string => !!daicd);

      // Create BathRoomSummary object
      const summary = new BathRoomSummary();
      summary.corpCd = corpCd;
      summary.officeCd = officeCd;
      summary.blockCd = master.BLOCKCD || '';
      summary.subCd = master.SUBCD || '';
      summary.unitCd = master.UNITCD || '';
      summary.bathRoomCd = bathRoomCd || '';
      summary.bathName = master.BATHNAME || null;
      summary.capacity = master.CAPACITY || null;
      summary.sortNo = master.SORTNO ?? null;
      summary.bathRule = master.BATHRULE?.toString() || null;
      summary.bathRuleXMLValue = master.BATHRULE ? (bathRuleToJson(master.BATHRULE.toString()) as BathRule[]) : null;
      summary.insStfCd = master.INSSTFCD || null;
      summary.insDate = master.INSDATE ? new Date(master.INSDATE) : null;
      summary.updStfCd = master.UPDSTFCD || null;
      summary.updDate = master.UPDDATE ? new Date(master.UPDDATE) : null;
      summary.daiList = daiList;

      return summary;
    });
  }

  /**
   * Get bath room rule from bathRuleXMLValue
   * Finds the selected rule and returns the corresponding BathRoomRule enum value
   * @param bathRuleXMLValue - Array of BathRule objects
   * @returns BathRoomRule enum value (None, WomenFirst, or MenFirst)
   */
  static getBathRoomConfigValue(bathRuleXMLValue: BathRule[] | null | undefined): BathRoomRule {
    if (!bathRuleXMLValue || bathRuleXMLValue.length === 0) {
      return BathRoomRule.None;
    }

    // Find the selected rule (selected = "1")
    const selectedRule = bathRuleXMLValue.find(rule => rule.selected === "1");

    if (!selectedRule || !selectedRule.type?.code) {
      return BathRoomRule.None;
    }

    // Map type code to BathRoomRule enum
    switch (selectedRule.type.code) {
      case "WOMEN_FIRST":
        return BathRoomRule.WomenFirst;
      case "MEN_FIRST":
        return BathRoomRule.MenFirst;
      case "NONE":
      default:
        return BathRoomRule.None;
    }
  }
  /**
   * Directly update avatar image source in DOM by daiCd
   * Bypasses Angular change detection by directly manipulating DOM
   * Finds all img elements with id="avatar-{daiCd}" and updates their src
   * @param daiCd - User's dai code
   * @param cdnUrl - Avatar CDN URL path (without base URL)
   */
  static updateAvatarByDaiCd(daiCd: string, cdnUrl: string): void {
    if (!daiCd || !cdnUrl) return;

    const fullAvatarUrl = `${environment.avatarUrl}/${cdnUrl}`;
    const avatarId = `avatar-${daiCd}`;

    // Find all avatar elements with this daiCd (there might be multiple in different tables)
    const avatarElements = document.querySelectorAll(`img[id="${avatarId}"]`);

    avatarElements.forEach((img: Element) => {
      if (img instanceof HTMLImageElement) {
        img.src = fullAvatarUrl;
      }
    });
  }
}

/**
 * Helper class for dark mode detection
 * Provides utilities to check and monitor dark mode state
 */
export class DarkModeHelper {
  /**
   * Check if dark mode is currently enabled
   * @returns true if dark mode is enabled
   */
  static isDarkModeEnabled(): boolean {
    const bodyClass = document.body.className;
    return bodyClass.includes("dark");
  }

  /**
   * Setup a MutationObserver to monitor dark mode changes
   * @param callback - Callback function to execute when dark mode changes
   * @returns MutationObserver instance
   */
  static setupDarkModeListener(callback: () => void): MutationObserver {
    const observer = new MutationObserver(callback);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return observer;
  }
}


export const BatheMethodList = [
  { id: BatheMethodOption.Normal, text: BATHE_MANAGEMENT.BATH_METHOD_NORMAL },
  { id: BatheMethodOption.Private, text: BATHE_MANAGEMENT.BATH_METHOD_PRIVATE },
  { id: BatheMethodOption.Chair, text: BATHE_MANAGEMENT.BATH_METHOD_CHAIR },
  { id: BatheMethodOption.Lift, text: BATHE_MANAGEMENT.BATH_METHOD_LIFT },
  { id: BatheMethodOption.Stretcher, text: BATHE_MANAGEMENT.BATH_METHOD_STRETCHER },
  { id: BatheMethodOption.Bed, text: BATHE_MANAGEMENT.BATH_METHOD_BED },
  { id: BatheMethodOption.Shower, text: BATHE_MANAGEMENT.BATH_METHOD_SHOWER }
];

export const BathePriorityList = [
  { id: BatheType.None, text: BATHE_MANAGEMENT.BATH_TYPE_NONE, cssClass: 'bathe-type-option--none' },
  { id: BatheType.First, text: BATHE_MANAGEMENT.BATH_TYPE_FIRST, cssClass: 'bathe-type-button--general' },
  { id: BatheType.Priority, text: BATHE_MANAGEMENT.BATH_TYPE_PRIORITY, cssClass: 'bathe-type-button--special' },
  { id: BatheType.Last, text: BATHE_MANAGEMENT.BATH_TYPE_LAST, cssClass: 'bathe-type-button--green' },
];



export function getTodayJPStr() {
  const jpFormatter = new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  });
  const now = new Date();
  const formattedNow = jpFormatter.format(now);
  const [year, month, day] = formattedNow.split('/');

  return `${year}-${month}-${day}`
}

export function formatDateToMMDDOrInfinity(dateStr: string | Date): string {
    const str = typeof dateStr === 'string' ? dateStr : dateStr.toISOString();
    const [datePart] = str.split(' ');
    const [year, mm, dd] = datePart.split('-');
    if (year === '2500') return '未定';
    return `${mm}/${dd}`;
}

/**
 * Format date to YYYY-MM-DD string using local timezone
 * @param date - Date to format
 * @returns Formatted date string
 */
export function formatDateToLocalString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format date to YYYY/MM/DD string for holiday comparison
 * @param date - The date to format
 * @returns Formatted date string (YYYY/MM/DD)
 */
export function formatDateToYYYYMMDD(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
}

/**
 * Determine day color type based on day of week and holidays
 * @param date - The date to check
 * @returns DayColorType enum value
 */
export function getDayColorType(date: Date, holidays: Holiday[]): DayColorType {
  const dayOfWeek = date.getDay();

  // Check if date is a holiday (format: YYYY/MM/DD)
  const dateStr = formatDateToYYYYMMDD(date);
  const isHoliday = holidays.some(holiday => holiday.holiday === dateStr);

  if (dayOfWeek === 0 || isHoliday) {
    return DayColorType.OffWork; // Sunday or Holiday - Red
  } else if (dayOfWeek === 6) {
    return DayColorType.Saturday; // Saturday - Blue
  } else {
    return DayColorType.Normal; // Weekday - Black
  }
}

/**
 * Format date range for weekly schedule display
 * @param startDate - Start date of the week
 * @param endDate - End date of the week
 * @returns Formatted string like '10/21（月）～10/27（日）'
 */
export function formatWeekRange(startDate: Date, endDate: Date): string {
  const formatDate = (date: Date): string => {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const dayJP = getJapaneseDayOfWeek(date.getDay());
    return `${month}/${day}（${dayJP}）`;
  };

  return `${formatDate(startDate)}～${formatDate(endDate)}`;
}


export function getJapaneseDayOfWeek(dayIndex: number): string {
  return BATHE_MANAGEMENT.DAYS_JP[dayIndex];
}


export function formatDate(date: Date): string {
  const month = date.getMonth() + 1; // getMonth() returns 0-11
  const day = date.getDate();
  const dayOfWeek = getJapaneseDayOfWeek(date.getDay());

  return `${month}/${day}(${dayOfWeek})`;
}

/**
 * Format date to yyyy/mm/dd HH:mm format
 */
export function formatDateTime(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}/${month}/${day} ${hours}:${minutes}`;
}



/**
 * Build result map with all requested dates
 */
export function buildResultMap(
  fromDate: Date,
  toDate: Date,
  bathRoomDateMap: Map<string, { bathDaily: BathDailyModel[]; bathDai: BathDaiModel[] }>
): Map<string, { bathDaily: BathDailyModel[]; bathDai: BathDaiModel[] }> {
  const resultMap = new Map<string, { bathDaily: BathDailyModel[]; bathDai: BathDaiModel[] }>();

  for (let date = new Date(fromDate); date <= toDate; date.setDate(date.getDate() + 1)) {
    const dateStr = formatDateToLocalString(date);
    const data = bathRoomDateMap.get(dateStr);

    if (data) {
      resultMap.set(dateStr, data);
    }
  }

  return resultMap;
}

/**
 * Parse bath daily API response
 */
export function parseBathDailyResponse(apiResult: any, dateKey: string): BathDailyModel[] {
  const bathDailyData: BathDailyModel[] = [];

  if (apiResult?.bathDaily && SUCCESS_STATUS.includes(apiResult.bathDaily?.status ?? 500)) {
    try {
      if (apiResult.bathDaily?.result?.isSuccessStatusCode && apiResult.bathDaily?.result?.json) {
        const dailyData = JSON.parse(apiResult.bathDaily.result.json) ?? [];

        bathDailyData.push(...dailyData.map((item: any) => {
          const model = new BathDailyModel();
          Object.assign(model, {
            corpcd: item.corpcd ?? '',
            officecd: item.officecd ?? '',
            blockcd: item.blockcd ?? '',
            subcd: item.subcd ?? '',
            unitcd: item.unitcd ?? '',
            daicd: item.daicd ?? '',
            bathRoomCd: item.bathRoomCd ?? 0,
            bathDate: parseLocalDate(item.bathDate) ?? new Date(),
            bathMethodCd: item.bathMethodCd ?? null,
            sortNo: item.sortNo ?? null,
            insStfcd: item.insStfcd ?? null,
            insDate: parseLocalDateTime(item.insDate),
            updStfcd: item.updStfcd ?? null,
            updDate: parseLocalDateTime(item.updDate)
          });
          return model;
        }));
      }
    } catch (error) {
      console.error(`Error mapping bath daily data for ${dateKey}:`, error);
    }
  }

  return bathDailyData;
}

/**
 * Parse bath dai API response
 */
export function parseBathDaiResponse(apiResult: any, dateKey: string): BathDaiModel[] {
  const bathDaiData: BathDaiModel[] = [];

  if (apiResult?.bathDai && SUCCESS_STATUS.includes(apiResult.bathDai?.status ?? 500)) {
    try {
      if (apiResult.bathDai?.result?.isSuccessStatusCode && apiResult.bathDai?.result?.json) {
        const daiData = JSON.parse(apiResult.bathDai.result.json) ?? [];

        bathDaiData.push(...daiData.map((item: any) => {
          const model = new BathDaiModel();
          Object.assign(model, {
            corpCd: item.corpCd ?? '',
            officeCd: item.officeCd ?? '',
            blockCd: item.blockCd ?? '',
            subCd: item.subCd ?? '',
            unitCd: item.unitCd ?? '',
            daiCd: item.daiCd ?? '',
            bathRoomCd: item.bathRoomCd ?? 0,
            bathMethodCd: item.bathMethodCd ?? null,
            bathWeeks: item.bathWeeks ?? null,
            bathDays: item.bathDays ?? null,
            priorityCd: item.priorityCd ?? BatheType.None,
            treatmentNotes: item.treatmentNotes ?? null,
            sortNo: item.sortNo ?? null,
            insStfCd: item.insStfCd ?? null,
            insDate: parseLocalDateTime(item.insDate),
            updStfCd: item.updStfCd ?? null,
            updDate: parseLocalDateTime(item.updDate)
          });
          return model;
        }));
      }
    } catch (error) {
      console.error(`Error mapping bath dai data for ${dateKey}:`, error);
    }
  }

  return bathDaiData;
}

/**
 * Parse date string as local timezone (not UTC) - for bathDate only (sets to midnight)
 * Converts "2025-11-07T00:00:00" to Date object with local timezone 2025-11-07 00:00:00
 * Instead of UTC which would become "2025-11-06T17:00:00.000Z" (with timezone offset)
 */
export function parseLocalDate(dateString: string | null): Date | null {
  if (!dateString) return null;

  // Remove 'Z' or timezone info if exists, then parse as local
  const cleanDateString = dateString.replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, '');

  // Parse date components manually to avoid timezone conversion
  const match = cleanDateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return new Date(dateString); // Fallback to default parsing

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1; // Month is 0-indexed
  const day = parseInt(match[3], 10);

  // Create date at midnight local time
  return new Date(year, month, day, 0, 0, 0, 0);
}

/**
 * Convert Date object or string to YYYY-MM-DD string format for API
 */
export function formatDateToString(date: Date | string | null): string {
  if (!date) return '';

  let dateObj: Date;
  if (typeof date === 'string') {
    // If already a string in YYYY-MM-DD format, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    // Otherwise parse it
    dateObj = new Date(date);
  } else {
    dateObj = date;
  }

  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Parse datetime string as local timezone (not UTC) - preserves time
 * Converts "2025-11-07T14:30:45" to Date object with local timezone 2025-11-07 14:30:45
 * Instead of UTC which would apply timezone offset
 */
export function parseLocalDateTime(dateTimeString: string | null): Date | null {
  if (!dateTimeString) return null;

  // Remove 'Z' or timezone info if exists
  const cleanDateString = dateTimeString.replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, '');

  // Parse full datetime components
  const match = cleanDateString.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}):(\d{2}))?/);
  if (!match) return new Date(dateTimeString); // Fallback

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1; // Month is 0-indexed
  const day = parseInt(match[3], 10);
  const hours = match[4] ? parseInt(match[4], 10) : 0;
  const minutes = match[5] ? parseInt(match[5], 10) : 0;
  const seconds = match[6] ? parseInt(match[6], 10) : 0;

  // Create date with time preserved in local timezone
  return new Date(year, month, day, hours, minutes, seconds, 0);
}

/**
 * Check if bathDays is over specified days from today
 * bathDays format: "2025-10-30 12:11" (datetime string)
 */
export function isBathDaysOverDays(bathDays: string | null, daysThreshold: number, currentDate: any): boolean {
  if (!bathDays) return false;

  try {
    // Parse the datetime string
    const bathDate = new Date(bathDays);

    // Check if valid date
    if (isNaN(bathDate.getTime())) return false;

    // Get current date at midnight for comparison
    const today = new Date(currentDate);
    today.setHours(0, 0, 0, 0);

    // Get bath date at midnight for comparison
    const bathDateMidnight = new Date(bathDate);
    bathDateMidnight.setHours(0, 0, 0, 0);

    // Calculate days difference (positive = past)
    const diffTime = today.getTime() - bathDateMidnight.getTime();
    const daysDiff = Math.round(diffTime / (1000 * 60 * 60 * 24));

    return daysDiff > daysThreshold;
  } catch (error) {
    console.error('Error checking bathDays threshold:', error);
    return false;
  }
}

/**
 * Count scheduled bath days from bathDays string
 * bathDays format: "0000000" where each digit represents Mon-Sun (1 = scheduled, 0 = not scheduled)
 * Example: "0000000" = 0 days, "0000001" = 1 day, "0001001" = 2 days
 * @param bathDays String of 7 characters (0 or 1)
 * @returns Number of scheduled bath days
 */
export function countScheduledBathDays(bathDays: string | null): number {
  if (!bathDays || bathDays.length !== 7) return 0;

  let count = 0;
  for (let i = 0; i < 7; i++) {
    if (bathDays[i] === '1') {
      count++;
    }
  }
  return count;
}

export function parseDaiTableToBathDais(daiTable: string): BathDais[] {
  try {
    if (!daiTable || typeof daiTable !== 'string' || daiTable.trim() === '') {
      return [];
    }

    const normalizedDaiTable = normalizeCSVMultilineQuotes(daiTable);
    const lines = normalizedDaiTable.split(/\r?\n/).filter(line => line.trim() !== '');

    if (lines.length === 0) {
      return [];
    }

    // Skip the first line (header) and process remaining lines as data rows
    const dataLines = lines.slice(1);

    const bathDaisArray: BathDais[] = [];

    for (const line of dataLines) {
      const values = parseCSVLine(line);

      if (values.length >= 6) {
        const bathDai = new BathDais();

        bathDai.DAICD = values[0] || undefined;
        const bathMethodCd = parseInt(values[1], 10);
        bathDai.BATHMETHODCD = !isNaN(bathMethodCd) ? bathMethodCd : undefined;
        bathDai.BATHWEEKS = values[2] || undefined;
        bathDai.BATHDAYS = values[3] || undefined;
        const priorityCd = parseInt(values[4], 10);
        bathDai.PRIORITYCD = !isNaN(priorityCd) ? priorityCd : undefined;
        bathDai.TREATMENTNOTES = values[5] || undefined;

        bathDaisArray.push(bathDai);
      }
    }

    return bathDaisArray;
  } catch (error) {
    console.error('parseDaiTableToBathDais error:', error);
    return [];
  }
}

export function normalizeCSVMultilineQuotes(csv: string): string {
  try {
    if (!csv || typeof csv !== 'string') {
      return '';
    }

    let result = '';
    let insideQuotes = false;

    for (let i = 0; i < csv.length; i++) {
      const char = csv[i];

      if (char === '"') {
        insideQuotes = !insideQuotes;
        result += char;
      } else if ((char === '\n' || char === '\r') && insideQuotes) {
        if (char === '\r' && csv[i + 1] === '\n') {
          i++;
        }
        result += ' ';
      } else {
        result += char;
      }
    }

    return result;
  } catch (error) {
    console.error('normalizeCSVMultilineQuotes error:', error);
    return '';
  }
}

export function parseCSVLine(line: string): string[] {
  try {
    if (!line || typeof line !== 'string') {
      return [];
    }

    const values: string[] = [];
    let currentValue = '';
    let insideQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        if (insideQuotes && line[i + 1] === '"') {
          // Escaped quote ("") inside a quoted field -> literal "
          currentValue += '"';
          i++;
        } else {
          insideQuotes = !insideQuotes;
        }
      } else if (char === ',' && !insideQuotes) {
        values.push(currentValue.trim());
        currentValue = '';
      } else {
        currentValue += char;
      }
    }

    // Push the last value
    values.push(currentValue.trim());

    return values;
  } catch (error) {
    console.error('parseCSVLine error:', error);
    return [];
  }
}

/**
 * Parse LogTable CSV string to array of DAIS objects
 * Similar to parseDaiTableToBathDais but for BathDaily LogTable
 * @param logTable - CSV string from BathDaily.LogTable
 * @returns Array of DAIS objects
 *
 * CSV Format:
 * - Old format (3 fields): DAICD,BATHMETHODCD,SORTNO
 * - New format (4 fields): DAICD,BATHMETHODCD,SORTNO,ISVALID
 *
 * ISVALID field (optional):
 * - "True" or "true" → true
 * - "False" or "false" → false
 * - Empty string or missing → undefined (backward compatibility)
 */
export function parseLogTableToDais(logTable: string): any[] {
  try {
    if (!logTable || typeof logTable !== 'string' || logTable.trim() === '') {
      return [];
    }

    const normalizedLogTable = normalizeCSVMultilineQuotes(logTable);
    const lines = normalizedLogTable.split(/\r?\n/).filter(line => line.trim() !== '');

    if (lines.length === 0) {
      return [];
    }

    // Skip the first line (header) and process remaining lines as data rows
    const dataLines = lines.slice(1);

    const daisArray: any[] = [];

    for (const line of dataLines) {
      const values = parseCSVLine(line);

      if (values.length >= 3) {
        // const bathMethodCd = parseInt(values[1], 10);
        const sortNo = parseInt(values[1], 10);

        // Parse ISVALID field (4th column) if available
        let isValid: boolean | undefined = undefined;
        if (values.length >= 3 && values[2] !== undefined && values[2] !== '') {
          // Convert string "True"/"False" to boolean
          isValid = values[2].toLowerCase() === 'true';
        }

        const dais = {
          DAICD: values[0] || undefined,
          // BATHMETHODCD: !isNaN(bathMethodCd) ? bathMethodCd : 0,
          SORTNO: !isNaN(sortNo) ? sortNo : 0,
          ISVALID: isValid, // undefined for backward compatibility with old data
        };

        daisArray.push(dais);
      }
    }

    return daisArray;
  } catch (error) {
    console.error('parseLogTableToDais error:', error);
    return [];
  }
}

/**
 * Map the JSON `dais` array a BathDaily response now carries into DAIS objects,
 * snapshot fields included. LogTable stays a three-column CSV (DAICD,SORTNO,ISVALID)
 * and must not be parsed for these fields - they only travel in this JSON array.
 * @param jsonDais - `result.dais` from get-bath-daily / upsert-logs, or undefined
 */
export function mapJsonDais(jsonDais: any[] | undefined | null): DAIS[] {
  if (!jsonDais || !Array.isArray(jsonDais)) {
    return [];
  }

  return jsonDais.map((d: any) => {
    const dais = new DAIS();
    dais.DAICD = d.daicd || undefined;
    dais.BATHMETHODCD = d.bathmethodcd ?? 0;
    dais.SORTNO = d.sortno ?? 0;
    dais.ISVALID = d.isvalid;
    dais.BATHWEEKS = d.bathweeks ?? null;
    dais.BATHDAYS = d.bathdays ?? null;
    dais.PRIORITYCD = d.prioritycd ?? null;
    dais.TREATMENTNOTES = d.treatmentnotes ?? null;
    dais.BLOCKCD = d.blockcd ?? null;
    dais.UNITCD = d.unitcd ?? null;
    dais.MASTERUPDDATE = d.masterupddate ?? null;
    dais.APPLYSEQ = d.applyseq ?? null;
    dais.NEEDSRECALC = d.needsrecalc ?? null;
    return dais;
  });
}



/**
 * Helper function to create a SidebarBlock item
 */
export function createSidebarBlock(
  daiCd: string,
  daiInfoMap: Map<string, any>,
  currentDateKey: string,
  time: string,
  remind: string,
  bathDaysOriginal: string | null,
  tags: Tag[],
  bathMasters: BathMasterItem[]
): SidebarBlock {
  const daiObj: any = daiInfoMap.get(daiCd);
  const name = daiObj ? `${daiObj.family || ''} ${daiObj.given || ''}`.trim() : daiCd;
  const gender = daiObj?.sex === 1 ? 1 : 0;
  const badge = getBadgeForDaiCd(daiCd, currentDateKey, bathMasters);

  return {
    daiCd,
    name,
    gender,
    badge,
    time,
    remind,
    bathDaysOriginal,
    isRestricted: tags.length > 0,
    tags
  };
}


/**
 * Get badge (bath method) for a specific daiCd at current date
 * Searches across all bathrooms in master data
 */
export function getBadgeForDaiCd(daiCd: string, dateKey: string, bathMasters: BathMasterItem[]): string {
  // Iterate through all bathrooms in master data
  const master = bathMasters.find(e => e.BATHDAIS.some(dai => dai.DAICD == daiCd))
  if (master) {
    const bathDai = master.BATHDAIS.find(e => e.DAICD == daiCd);
    return BatheHelper.getMethodFromBatheOption(bathDai?.BATHMETHODCD as BatheMethodOption);
  }

  return '';
}



/**
 * Build tags array for bath action
 * - Add "前回入浴中止" tag if type == "bathe_cancel"
 * - Add "restriction" tag if bathDays is over 4 days from today
 */
export function buildTagsForBathAction(bathAction: BatheImplement, currentDate: Date): Tag[] {
  const tags: Tag[] = [];

  // Check if type is "bathe_cancel"
  if (bathAction.type === 'bathe_cancel') {
    tags.push({
      label: '前回入浴中止',
      type: 'restriction'
    });
  }

  // Check if bathDays is over 4 days from today (only for bathe_implement type)
  if (bathAction.type === 'bathe_implement' && isBathDaysOverDays(bathAction.recordDate, 3, new Date())) {
    tags.push({
      label: '4日以上',
      type: 'restriction'
    });
  }

  return tags;
}



/**
 * Check if a given date matches the bathWeeks and bathDays criteria
 * @param date The date to check
 * @param bathWeeks String of 5 characters (0 or 1) representing weeks 1-5 of the month
 * @param bathDays String of 7 characters (0 or 1) representing Mon-Sun
 * @returns true if the date matches both criteria, false if null/empty or doesn't match
 * Note: If isNyuSho is true, bathWeeks is bypassed (only bathDays is checked)
 */
export function isDateMatchingBathSchedule(date: Date, bathWeeks: string | null, bathDays: string | null, isNyuSho: boolean): boolean {
  // bathDays is always required
  if (!bathDays) {
    return false;
  }

  // Validate bathDays format
  if (bathDays.length !== 7) {
    return false;
  }

  // Check day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  // bathDays format: index 0 = Monday, index 6 = Sunday
  const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const bathDayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert to Mon=0, Sun=6

  if (bathDays[bathDayIndex] !== '1') {
    return false; // Day of week doesn't match
  }

  // If isNyuSho is true, bypass bathWeeks check (only check bathDays)
  if (isNyuSho) {
    return true;
  }

  // For non-NyuSho offices, check bathWeeks
  // Allow all weeks for '00000' (no restriction) or '11111' (all weeks)
  if (!bathWeeks || Utils.isNullOrEmpty(bathWeeks) || bathWeeks === '00000' || bathWeeks === '11111') {
    return true;
  }

  // Validate bathWeeks format
  if (bathWeeks.length !== 5) {
    return false;
  }

  // Check week of month (1-5)
  const dayOfMonth = date.getDate();
  const weekOfMonth = Math.ceil(dayOfMonth / 7); // 1-5
  const weekIndex = weekOfMonth - 1; // 0-4 for array indexing

  if (weekIndex < 0 || weekIndex >= 5) {
    return false; // Invalid week
  }

  if (bathWeeks[weekIndex] !== '1') {
    return false; // Week doesn't match
  }

  return true; // Both day and week match
}

/**
 * Check if a dai is scheduled for bath on a specific date using the bath scheduled matrix
 * @param matrix - The bath scheduled matrix (2D array with header row containing dates)
 * @param daiCd - The dai code to check
 * @param checkDate - The date to check
 * @param bathWeeks - Bath weeks setting (5 char string)
 * @param bathDays - Bath days setting (7 char string)
 * @param isNyuSho - Whether the facility is NyuSho type
 * @returns true if the dai is scheduled for bath on the given date
 */
export function isDaiScheduledOnDate(
  matrix: string[][],
  daiCd: string,
  checkDate: Date,
  bathWeeks: string | null,
  bathDays: string | null,
  isNyuSho: boolean,
  isSyncFlow: boolean = false
): boolean {
  // Convert checkDate to yyyy-mm-dd format
  const dateKey = formatDateToLocalString(checkDate);

  // Get header row (dates)
  const headerRow = matrix[0];
  if (!headerRow) {
    return false;
  }

  // Find column index for the date
  const dateColumnIndex = headerRow.indexOf(dateKey);
  if (dateColumnIndex === -1) {
    return false;
  }

  // Find row for the daiCd
  const daiRow = matrix.find(row => row[0] === daiCd);
  if (!daiRow) {
    return false;
  }

  // Get the schedule value at the date column
  const scheduleValue = daiRow[dateColumnIndex];

  // Base case: cancel (any '1x') or active but no check ('21') => skip
  // Rule 3a: active yotei + no bath setting ('20') => pass only in sync flow
  if (scheduleValue?.startsWith('1') || scheduleValue === '21') {
    return false;
  }
  if (isSyncFlow && scheduleValue === '20') {
    return true;
  }

  // Case 1: active yotei + bath pass => completed
  if (scheduleValue === '22') {
    return true;
  }

  // Remaining: '00' (default), '01'/'02' (no yotei, new states - handled later)
  // => fall through to bathDays/bathWeeks check

  // Case 2: bathDays matching AND (isNyuSho OR bathWeeks matching)

  // bathDays: null/empty/'0000000' = no restriction (pass), else check day of week
  const dayOfWeek = checkDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const bathDayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert to Mon=0, Sun=6

  const isBathDaysSatisfied = bathDays &&
    (bathDays.length === 7 && bathDays[bathDayIndex] === '1');

  if (!isBathDaysSatisfied) {
    return false;
  }

  if (isNyuSho) {
    return true;
  }

  if (!bathWeeks || Utils.isNullOrEmpty(bathWeeks) || bathWeeks === '00000' || bathWeeks === '11111') {
    return true;
  }

  const dayOfMonth = checkDate.getDate();
  const weekOfMonth = Math.ceil(dayOfMonth / 7); // 1-5
  const weekIndex = weekOfMonth - 1; // 0-4 for array indexing

  if (weekIndex < 0 || weekIndex >= 5) {
    return false; // Invalid week
  }

  return bathWeeks.length === 5 && bathWeeks[weekIndex] === '1';
}

/**
 * The single valid/invalid rule shared by the two bathing screens (bathing-schedule and
 * bathe-management), kept here so both resolve identity the same way. A dai is valid on a date when
 * its daibelong+yotei is active (isDaiValidOnDate) and - for Tsusho only - the bath schedule matrix
 * says it bathes that date (isDaiScheduledOnDate, sync-flow so an active-yotei/no-setting cell still
 * passes). Nyusho stops at the yotei check. For Tsusho the schedule check always runs: an absent or
 * empty matrix means no one is scheduled, so isDaiScheduledOnDate returns false and the dai is
 * invalid (matrix is defaulted to [] only to avoid a null deref). bathWeeks/bathDays must come from
 * the resolved (snapshot-first) config so a frozen day stays consistent across both screens.
 */
export function isDaiValidForDisplayOnDate(params: {
  daiCd: string;
  checkDate: Date;
  dateStr: string;
  isNyuSho: boolean;
  bathWeeks: string | null;
  bathDays: string | null;
  bathScheduledMatrix: string[][] | null | undefined;
  isDaiValidOnDate: (daiCd: string, dateStr: string) => boolean;
}): boolean {
  let valid = params.isDaiValidOnDate(params.daiCd, params.dateStr);
  if (valid && !params.isNyuSho) {
    valid = isDaiScheduledOnDate(
      params.bathScheduledMatrix ?? [],
      params.daiCd,
      params.checkDate,
      params.bathWeeks,
      params.bathDays,
      params.isNyuSho,
      true
    );
  }
  return valid;
}

/**
 * Check if matrix value for a daiCd at a specific date starts with "1"
 */
export function isDaiScheduledAtDate(
  matrix: string[][],
  daiCd: string,
  checkDate: Date
): boolean {
  const dateKey = formatDateToLocalString(checkDate);

  const headerRow = matrix[0];
  if (!headerRow) {
    return false;
  }

  const dateColumnIndex = headerRow.indexOf(dateKey);
  if (dateColumnIndex === -1) {
    return false;
  }

  const daiRow = matrix.find(row => row[0] === daiCd);
  if (!daiRow) {
    return false;
  }

  const scheduleValue = daiRow[dateColumnIndex];
  return scheduleValue?.startsWith('2') ?? false;
}

/**
 * Enrich bathScheduledMatrix by combining with userDateMatrix.
 * Rule: if bathScheduledMatrix cell is '00' AND userDateMatrix cell normalizes to '11' → set to '20'
 * Supports BatheManagement format ('11') and BathingSchedule format ('1|09:30-16:00').
 * Returns a new matrix (does not mutate the original).
 */
export function enrichBathScheduledMatrix(
  bathMatrix: string[][],
  userMatrix: string[][],
  syncTime?: string
): string[][] {
  if (bathMatrix.length === 0 || userMatrix.length === 0) {
    return bathMatrix;
  }

  const bathHeader = bathMatrix[0];
  const userHeader = userMatrix[0];

  const result: string[][] = bathMatrix.map((row, rowIdx) => {
    if (rowIdx === 0) return [...row]; // copy header as-is

    const daiCd = row[0];
    const userRowIdx = userMatrix.findIndex((r, i) => i > 0 && r[0] === daiCd);
    if (userRowIdx === -1) return [...row];

    return row.map((cell, bathColIdx) => {
      if (bathColIdx === 0) return cell; // daiCd column

      const normalizedCell = normalizeTimedCell(cell, syncTime);
      if (normalizedCell !== '00') return normalizedCell; // only enrich '00' cells

      const dateKey = bathHeader[bathColIdx];
      const userColIdx = userHeader.indexOf(dateKey);
      if (userColIdx === -1) return normalizedCell;

      return normalizeUserDateCell(userMatrix[userRowIdx][userColIdx], syncTime) === '11' ? '20' : normalizedCell;
    });
  });

  return result;
}

function normalizeUserDateCell(cell: string | undefined, syncTime?: string): string {
  if (!cell) return '00';

  // BatheManagement format: '{daiBelong}{yotei}', e.g. '11'.
  if (cell.length === 2 && !cell.includes('|')) {
    return cell;
  }

  return normalizeTimedCell(cell, syncTime);
}

function normalizeTimedCell(cell: string | undefined, syncTime?: string): string {
  if (!cell) return '00';

  // Empty BathingSchedule range means daiBelong is known but no active yotei range exists.
  if (/^[01]\|$/.test(cell)) {
    return `${cell[0]}0`;
  }

  if (!/^[01]\|\d{1,2}:\d{2}-\d{1,2}:\d{2}(,\d{1,2}:\d{2}-\d{1,2}:\d{2})*$/.test(cell)) {
    return cell;
  }

  // BathingSchedule format: '{daiBelong}|{yoteiRange1,yoteiRange2,...}'.
  const [daiBelong, yoteiRanges] = cell.split('|');
  const daiBelongValue = daiBelong === '1' ? '1' : '0';
  const hasActiveYotei = !!syncTime && yoteiRanges
    .split(',')
    .some(range => isTimeInRange(syncTime, range.trim()));

  return `${daiBelongValue}${hasActiveYotei ? '1' : '0'}`;
}

function isTimeInRange(time: string, range: string): boolean {
  const [start, end] = range.split('-');
  if (!start || !end) return false;

  const timeMinutes = toMinutes(time);
  const startMinutes = toMinutes(start);
  const endMinutes = toMinutes(end);

  if (timeMinutes === null || startMinutes === null || endMinutes === null) {
    return false;
  }

  if (startMinutes <= endMinutes) {
    return timeMinutes >= startMinutes && timeMinutes <= endMinutes;
  }

  return timeMinutes >= startMinutes || timeMinutes <= endMinutes;
}

function toMinutes(time: string): number | null {
  const match = time.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }

  return hours * 60 + minutes;
}

/**
 * Helper function to compare two arrays for equality
 */
export function areArraysEqual(arr1: string[], arr2: string[]): boolean {
  if (arr1.length !== arr2.length) {
    return false;
  }

  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) {
      return false;
    }
  }

  return true;
}

/**
 * Calculate the start and end dates of the current week based on weeklyScheduleRadioValue
 * @param today - The reference date (usually current date)
 * @param startOfWeek - Whether week starts on Monday or Sunday
 * @returns Object containing startDate and endDate of the week
 *
 * Logic:
 * - StartOfWeek.Monday: Week runs from Monday to Sunday
 *   - Normal case: From Monday of current week to Sunday of current week
 *   - If today is Sunday: From Monday of this week to today (Sunday)
 *
 * - StartOfWeek.Sunday: Week runs from Sunday to Saturday
 *   - Normal case: From Sunday of previous week to Saturday of current week
 *   - If today is Sunday: From today (Sunday) to Saturday of next week
 */
export function getWeekRange(today: Date, startOfWeek: StartOfWeek): { startDate: Date; endDate: Date } {
  const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  let startDate: Date;
  let endDate: Date;

  if (startOfWeek === StartOfWeek.Monday) {
    // Week starts on Monday
    if (dayOfWeek === 0) {
      // Today is Sunday - special case
      // Start from Monday of this week
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 6); // Go back 6 days to Monday
      // End is today (Sunday)
      endDate = new Date(today);
    } else {
      // Normal case: Monday to Sunday
      // Calculate days since Monday (1=0 days, 2=1 day, ..., 6=5 days, 0=6 days)
      const daysSinceMonday = dayOfWeek - 1;

      // Start from Monday of current week
      startDate = new Date(today);
      startDate.setDate(today.getDate() - daysSinceMonday);

      // End on Sunday of current week
      endDate = new Date(today);
      endDate.setDate(today.getDate() + (7 - dayOfWeek));
    }
  } else {
    // Week starts on Sunday (StartOfWeek.Sunday)
    if (dayOfWeek === 0) {
      // Today is Sunday - special case
      // Start from today (Sunday)
      startDate = new Date(today);
      // End on Saturday of next week
      endDate = new Date(today);
      endDate.setDate(today.getDate() + 6);
    } else {
      // Normal case: Sunday to Saturday
      // Start from Sunday of previous week
      startDate = new Date(today);
      startDate.setDate(today.getDate() - dayOfWeek);

      // End on Saturday of current week
      endDate = new Date(today);
      endDate.setDate(today.getDate() + (6 - dayOfWeek));
    }
  }

  return { startDate, endDate };
}

/**
 * Check if a user's daibelong record is valid for the target date
 * @param daiCd - User's daicd
 * @param targetDateStr - Target date string (YYYY-MM-DD)
 * @returns true if user has valid daibelong record for the target date, false otherwise
 */
export function isDaiBelongValidForDate(daiCd: string, targetDateStr: string, daiBelongUsers: any[]): boolean {
  if (!daiCd || !targetDateStr) {
    return false;
  }

  // Find daibelong records for this daicd
  const daiBelongRecords = daiBelongUsers.filter((record: any) => record.daicd === daiCd);

  if (!daiBelongRecords || daiBelongRecords.length === 0) {
    return false;
  }

  // Parse target date
  const targetDate = new Date(targetDateStr);
  targetDate.setHours(0, 0, 0, 0);

  // Check if target date falls within any daibelong record's date range
  return daiBelongRecords.some((record: any) => {
    const sdate = new Date(record.sdate);
    sdate.setHours(0, 0, 0, 0);

    const edate = new Date(record.edate);
    edate.setHours(0, 0, 0, 0);

    // Check if targetDate is between sdate and edate (inclusive)
    return targetDate >= sdate && targetDate <= edate;
  });
}

/**
 * Count bath daily logs in current week for a specific daiCd
 */
export function countBathDailyLogsInWeek(daiCd: string, weekDateKeys: string[], weeklyColumns: WeeklyColumn[]): number {
  let count = 0;
  // Check UI state in weeklyColumns instead of cache to avoid race condition
  // when items are dropped but auto-save hasn't completed yet
  weekDateKeys.forEach(dateKey => {
    const column = weeklyColumns.find(col => col.dateStr === dateKey);
    if (column?.daiList?.some(dai => dai.daiCd === daiCd)) {
      count++;
    }
  });
  return count;
}


/**
 * Check if a user's yotei config is valid for the target date
 * @param daiCd - User's daicd
 * @param targetDateStr - Target date string (YYYY-MM-DD)
 * @returns true if user has valid yotei config for the target date, false otherwise
 */
export function isYoteiValidForDate(daiCd: string, targetDateStr: string, userByYoteiConfigs: UserYoteiConfig[]): boolean {
  if (!daiCd || !targetDateStr) {
    return false;
  }

  // Find yotei config records for this objcd (daicd)
  const yoteiRecords = userByYoteiConfigs.filter((record: any) => record.objcd === daiCd);

  if (!yoteiRecords || yoteiRecords.length === 0) {
    return false;
  }

  // Parse target date
  const targetDate = new Date(targetDateStr);
  targetDate.setHours(0, 0, 0, 0);

  // Check if target date falls within any yotei record's date range
  return yoteiRecords.some((record: any) => {
    const sdate = new Date(record.sdate);
    sdate.setHours(0, 0, 0, 0);

    const edate = new Date(record.edate);
    edate.setHours(0, 0, 0, 0);

    // Check if targetDate is between sdate and edate (inclusive)
    return targetDate >= sdate && targetDate <= edate;
  });
}

/**
 * Get the merged yotei period for a daiCd by taking the latest record and
 * extending backwards through contiguous (adjacent) records.
 * Two records are considered contiguous when record[i].edate === record[i+1].sdate
 * (they touch with no gap), so they count as one continuous period.
 *
 * @param daiCd - The resident code
 * @param userByYoteiConfigs - Full list of yotei config records
 * @returns Merged { sdate, edate } or null if no records found
 */
export function getMergedYoteiConfig(
  daiCd: string,
  userByYoteiConfigs: UserYoteiConfig[], currentDate?: Date, 
  weekMode: boolean = false, weeklyScheduleRadioValue = StartOfWeek.Monday,
  sessionWeek = 0
): { sdate: string | Date; edate: string | Date } | null {
  const records = userByYoteiConfigs.filter(c => c.objcd === daiCd);
  if (records.length === 0) return null;

  // Sort by sdate descending (latest first)
  const sorted = [...records].sort(
    (a, b) => new Date(b.sdate).getTime() - new Date(a.sdate).getTime()
  );

  // Start with the latest record
  let mergedSdate = sorted[0].sdate;
  let mergedEdate = sorted[0].edate;

  // Walk backwards: extend start if a record's edate exactly touches mergedSdate
  for (let i = 1; i < sorted.length; i++) {
    const candidateEdateMs = new Date(sorted[i].edate).getTime();
    const chainStartMs = new Date(mergedSdate).getTime();
    if (candidateEdateMs === chainStartMs) {
      mergedSdate = sorted[i].sdate;
    } else {
      break;
    }
  }

  if (weekMode && currentDate != null) {
    var today = new Date();
    if (sessionWeek != 0) {
      today.setDate(today.getDate() + sessionWeek*7);
    }

    const { startDate, endDate } = getWeekRange(today, weeklyScheduleRadioValue);

    if (new Date(mergedSdate).getTime() > new Date(endDate.setHours(23,59,0,0)).getTime() 
      || new Date(mergedEdate).getTime() < new Date(startDate.setHours(0,0,0,0)).getTime()) {
      for (var x of sorted) {
        if ((new Date(x.sdate).getTime() <= new Date(startDate.setHours(0,0,0,0)).getTime() 
          && new Date(x.edate).getTime() >= new Date(startDate.setHours(0,0,0,0)).getTime())
        || (new Date(x.sdate).getTime() <= new Date(endDate.setHours(23,59,0,0)).getTime() 
          && new Date(x.edate).getTime() >= new Date(endDate.setHours(23,59,0,0)).getTime())) {
          mergedSdate = x.sdate;
          mergedEdate = x.edate;
          break;
        }
      }
    }
  }
  else if (currentDate != null && (new Date(mergedSdate).getTime() > new Date(currentDate.setHours(0,0,0,0)).getTime() 
    || new Date(mergedEdate).getTime() < new Date(currentDate.setHours(0,0,0,0)).getTime())) {
    for (var x of sorted) {
      if (new Date(x.sdate).getTime() <= new Date(currentDate.setHours(0,0,0,0)).getTime() 
      && new Date(x.edate).getTime() >= new Date(currentDate.setHours(0,0,0,0)).getTime()) {
        mergedSdate = x.sdate;
        mergedEdate = x.edate;
        break;
      }
    }
  }

  return { sdate: mergedSdate, edate: mergedEdate };
}

/**
 * Adjust order values for items when bathDaily data exists
 * Priority is given to sortNo from bathDaily, other items are reordered accordingly
 * Example: bathDai: 1-1, 2-2, 3-3, 4-4, bathDaily: 2-4
 * Result: 1-1, 3-2, 4-3, 2-4
 */
export function adjustOrderForBathDaily(sortedList: ExtendedBathDaiModel[]): ExtendedBathDaiModel[] {
  const result: ExtendedBathDaiModel[] = [];
  let currentOrder = 1;

  for (const item of sortedList) {
    // Clone the item to avoid mutation
    const clonedItem = { ...item };

    // If item has sortNo from bathDaily, use it as both sortNo and order
    if (clonedItem.sortNo == null) {
      // For items without bathDaily, assign sequential order
      // Skip order numbers that are already taken by bathDaily items
      while (sortedList.some(x => x.sortNo === currentOrder)) {
        currentOrder++;
      }
      clonedItem.sortNo = currentOrder;
      currentOrder++;
    }

    result.push(clonedItem);
  }

  // Sort by order to ensure correct display sequence
  return result.sort((a, b) => (a.sortNo ?? 0) - (b.sortNo ?? 0));
}

/**
 * Reassign sortNo for bathdaily items:
 * - Valid items (isValidForDisplay !== false): sortNo = 1, 2, 3, ... (placed first)
 * - Invalid items (isValidForDisplay === false): sortNo = -999 (placed last)
 *
 * This ensures invalid items are pushed to the end but still preserved for audit trail.
 * When displaying, we filter items with sortNo !== -999 or isValidForDisplay !== false.
 * When saving, we save ALL items (valid + invalid with sortNo -999).
 *
 * @param items - Array of ExtendedBathDaiModel
 * @returns Array with reassigned sortNo (valid items first, invalid items last)
 */
export function reassignSortNoWithInvalidAtEnd(items: ExtendedBathDaiModel[]): ExtendedBathDaiModel[] {
  if (!items || items.length === 0) {
    return [];
  }

  // Separate valid and invalid items
  const validItems = items.filter(item => item.isValidForDisplay !== false);
  const invalidItems = items.filter(item => item.isValidForDisplay === false);

  // Assign sortNo for valid items (1, 2, 3, ...)
  const validWithSortNo = validItems.map((item, index) => ({
    ...item,
    sortNo: index + 1
  }));

  // Assign sortNo = -999 for invalid items
  const invalidWithSortNo = invalidItems.map(item => ({
    ...item,
    sortNo: -999
  }));

  // Combine: valid items first, invalid items last
  return [...validWithSortNo, ...invalidWithSortNo];
}

/**
 * The DAICDs a dai move forced valid on this day (BathDailyItem.FORCEVALIDDAIS), or null when the
 * day renders normally: the list is absent/empty, or the day is past (a past day keeps its stored
 * rows, like NEEDSRECALC).
 */
export function getForceValidDaiCds(bathDaily: Pick<BathDailyItem, 'FORCEVALIDDAIS'> | null | undefined, isPast: boolean): Set<string> | null {
  const daiCds = (bathDaily?.FORCEVALIDDAIS ?? []).filter(daiCd => !!daiCd);
  return daiCds.length > 0 && !isPast ? new Set(daiCds) : null;
}

/**
 * Display list of a force-valid day: the generated rows (already filtered and sorted by the
 * screen's no-BathDaily path) without the forced DAICDs, then the forced rows - built from the
 * day's own entries - always valid, ordered by their stored SORTNO (the flag is an unordered set).
 * sortNo is renumbered 1..n over the result.
 */
export function mergeForceValidRecords<T extends { daiCd?: string | null; user?: { daicd?: string }; sortNo?: number | null; isValidForDisplay?: boolean }>(
  generated: T[],
  forced: T[],
  forceSet: Set<string>
): T[] {
  const daiCdOf = (item: T) => item.daiCd || item.user?.daicd || '';
  // A previously invalid entry (-999) or one without SORTNO goes after the ordered ones.
  const storedOrder = (item: T) => (item.sortNo == null || item.sortNo === -999) ? Number.MAX_SAFE_INTEGER : item.sortNo;
  const kept = generated.filter(item => !forceSet.has(daiCdOf(item)));
  const forcedSorted = forced
    .filter(item => forceSet.has(daiCdOf(item)))
    .sort((a, b) => storedOrder(a) - storedOrder(b));
  return [...kept, ...forcedSorted.map(item => ({ ...item, isValidForDisplay: true }))]
    .map((item, index) => ({ ...item, sortNo: index + 1 }));
}

//CKWEB-1961 đối ứng bỏ BlockName khỏi danh sách con của list BlockUnit
export function removeParentName(data: any[]) {
  return data.map(item => {
    const value = item.value ?? '';
    const name = item.name ?? '';

    // nếu value không có '-' thì đây là Block -> giữ nguyên
    if (!value.includes('-')) {
      return item;
    }

    // lấy parent name (phần trước dấu space)
    const parentName = name.split(' ')[0];

    // xoá (parentName + space) khỏi name
    const unitName = name.replace(parentName + ' ', '');

    return {
      ...item,
      name: unitName
    };
  });
}

export function sortBathBySortNo(data: any[]): any[] {
  if (!Array.isArray(data)) return [];

  return data.sort((a, b) => {
    const aHasSort = a?.sortNo !== null && a?.sortNo !== undefined;
    const bHasSort = b?.sortNo !== null && b?.sortNo !== undefined;
    // 1. sort theo sortNo
    if (aHasSort && bHasSort) {
      return a.sortNo - b.sortNo;
    }

    // 1 trong 2 có sortNo -> ưu tiên thằng có
    if (aHasSort) return -1;
    if (bHasSort) return 1;

    // 2. sort theo insDate
    if (a?.insDate && b?.insDate) {
      const timeA = new Date(a.insDate).getTime();
      const timeB = new Date(b.insDate).getTime();

      if (timeA !== timeB) {
        return timeA - timeB;
      }
    }

    // 1 trong 2 có insDate -> ưu tiên thằng có
    if (a?.insDate) return -1;
    if (b?.insDate) return 1;

    // 3. Cuối cùng sort theo label
    return (a?.label || '').localeCompare(b?.label || '');
  });
}
